import { Injectable } from "@angular/core";
import {
  ExperimentDetailsFormModel,
  ExperimentDesignFormModel,
  TreatmentFormModel,
  PlantingInstructionsFormModel,
  PottingInstructionsFormModel,
  PlantHandlingInstructionsFormModel,
  SeedNeedsFormModel,
  FlatInstructionsFormModel,
  PollinationPreferenceGroupFormModel,
  PollinationPreferenceGroupItemFormModel,
  SeedNeedsTreatmentFormModel,
  RequestedSamplingFormModel,
  PollinationInstructionsFormModel,
  SprayBoothStudyFormModel,
  ActiveFormModel,
  AdjuvantFormModel
} from "./request-form.service";
import {
  RequestDetails,
  RequestDesign,
  Treatment,
  RecurrenceDays,
  RequestedSeed,
  PollinationPreferenceGroup,
  PollinationPreferenceGroupItem,
  RequestedSeedTreatment,
  RequestedSampling,
  PollinationInstructions,
  OriginatingSystem,
  AdvancementPreference
} from "../../shared/services/request.service";
import { asEnumerable } from "linq-es2015";
import { isNullOrUndefined } from "util";

@Injectable()
export class RequestFormModelService {

  constructor() { }

  public toDetailsFormModel(details: RequestDetails): ExperimentDetailsFormModel {
    return {
      ceSite: details.ceSiteId,
      crop: details.cropId,
      description: details.description,
      delegate: details.delegateId,
      plantingDate: details.requestedPlantingDate,
      pmoProgram: details.programKey,
      pmoProject: details.projectKey,
      productConcept: details.productConceptIds.length > 0 ? details.productConceptIds[0] : null,
      requestPurpose: details.requestPurposeId,
      seedPacketDate: details.requestedSeedPacketDate,
      weeksOfColdTreatment: details.weeksOfColdTreatment,
      title: details.title,
      regulatoryStatus: details.regulatoryStatus,
      ppqRequired: details.ppqRequired,
      phytoInspectionRequired: details.phytoInspectionRequired
    };
  }

  public toDesignFormModel(design: RequestDesign): ExperimentDesignFormModel {
    let seedNeeds: SeedNeedsFormModel[] = [];

    if (design.requestedSeed && design.requestedSeed.length) {
      seedNeeds = asEnumerable(design.requestedSeed)
        .OrderBy(s => s.entryNumber)
        .Select(s => <SeedNeedsFormModel>{
          entryNumber: s.entryNumber,
          siid: s.siid,
          geName: s.geName,
          numberOfSeeds: s.numberOfSeeds,
          treatments: s.treatments ? asEnumerable(s.treatments)
            .Select(t => <SeedNeedsTreatmentFormModel>{
              treatmentNumber: t.treatmentNumber,
              seedCount: t.seedCount
            })
            .ToArray() : []
        })
        .ToArray();
    }

    const samplingsArray: RequestedSamplingFormModel[] = [];
    if (design.requestedSamplings && design.requestedSamplings.length > 0) {
      let sortedSamplings = design
        .requestedSamplings
        .sort((t1, t2) => {
          if (t1.order > t2.order)
            return 1;
          else if (t1.order < t2.order)
            return -1;
          else
            return 0;
        });

      let formModels = sortedSamplings.map(m => <RequestedSamplingFormModel>{
        assay: m.laboratoryAssayId,
        laboratory: m.laboratoryId,
        order: m.order,
        tissueType: m.tissueTypeId,
        tissueCollectionType: m.tissueCollectionType,
        growthStage: m.growthStageId,
        daysAfterPlanting: m.daysAfterPlanting
      });

      samplingsArray.push(...formModels);
    }


    const treatmentArray: TreatmentFormModel[] = [];

    if (design.treatments) {
      design.treatments
        .sort((t1, t2) => {
          if (t1.treatmentNumber > t2.treatmentNumber) {
            return 1;
          } else if (t1.treatmentNumber < t2.treatmentNumber) {
            return -1;
          } else {
            return 0;
          }
        })
        .forEach(t => {
          let treatmentModel: TreatmentFormModel = {
            plantingInstructions: null,
            flatInstructions: null,
            sprayBoothStudy: null,
            pottingInstructions: {
              potType: null,
              irrigation: null,
              fertigation: null,
              substrate: null
            },
            plantHandlingInstructions: null,
            treatmentNumber: t.treatmentNumber
          };
          if (t.plantingInstructions) {
            let plantingModel: PlantingInstructionsFormModel = {
              numberToHarvest: t.plantingInstructions.plantsToHarvest,
              numberToPots: t.plantingInstructions.plantsToPots,
              numberToStart: t.plantingInstructions.seedsToStart
            };
            treatmentModel.plantingInstructions = plantingModel;
          }
          treatmentModel.sprayBoothStudy = [];

          if (t.sprayBoothStudy && t.sprayBoothStudy.length > 0) {
            treatmentModel.sprayBoothStudy = t.sprayBoothStudy.map(s => ({
              actives: s.actives.map(a => ({
                activeId: a.activeId,
                rateValue: a.rateValue,
                rateUnitId: a.rateUnitId,
                relativeFieldRate: a.relativeFieldRate
              }) as ActiveFormModel),
              adjuvants: s.adjuvants.map(ad => ({
                adjuvantId: ad.adjuvantId,
                adjuvantValue: ad.adjuvantValue,
              }) as AdjuvantFormModel),
              applicationTimepointId: s.applicationTimepointId,
              applicationVolumeId: s.applicationVolumeId
            } as SprayBoothStudyFormModel));
          }

          if (t.flatInstructions) {
            let flatInstructionModel: FlatInstructionsFormModel = {
              directSeedToPots: t.flatInstructions.directSeedToPots,
              flatType: t.flatInstructions.flatTypeId,
              fertigation: t.flatInstructions.fertigationMethodId,
              irrigation: t.flatInstructions.irrigationMethodId,
              substrate: t.flatInstructions.substrateId
            };
            treatmentModel.flatInstructions = flatInstructionModel;
          }
          if (t.pottingInstructions) {
            let pottingModel: PottingInstructionsFormModel = {
              potType: t.pottingInstructions.potTypeId,
              fertigation: t.pottingInstructions.fertigationMethodId,
              irrigation: t.pottingInstructions.irrigationMethodId,
              substrate: t.pottingInstructions.substrateId
            };
            treatmentModel.pottingInstructions = pottingModel;
          }
          if (t.plantHandlingInstructions) {
            let plantHandlingModel: PlantHandlingInstructionsFormModel = {
              lighting: t.plantHandlingInstructions.lightingInstructionId,
              temperature: t.plantHandlingInstructions.temperatureInstructionId,
              humidity: t.plantHandlingInstructions.humidityInstructionId,
              imagingNeeded: t.plantHandlingInstructions.imagingNeeded,
            };
            treatmentModel.plantHandlingInstructions = plantHandlingModel;
          }
          treatmentArray.push(treatmentModel);
        });
    }

    var pollinationInstructions: PollinationInstructionsFormModel = {
      pollinationPreferences: [],
      pollinationCategory: null,
      donorsNeeded: false
    };

    if (design.pollinationInstructions) {
      pollinationInstructions = {
        pollinationPreferences: asEnumerable(design.pollinationInstructions.preferences)
          .Select(g => <PollinationPreferenceGroupFormModel>{
            orderNumber: g.orderNumber,
            conjunction: g.conjunction,
            items: asEnumerable(g.items)
              .Select(i => <PollinationPreferenceGroupItemFormModel>{
                orderNumber: i.orderNumber,
                conjunction: i.conjunction,
                pollinationType: i.pollinationTypeId,
                geName: i.geName
              })
              .ToArray()
          })
          .ToArray(),
        donorsNeeded: design.pollinationInstructions.donorsNeeded,
        pollinationCategory: design.pollinationInstructions.pollinationCategoryId
      }
    }

    return {
      numberOfReps: design.numberOfReps,
      numberOfSeedsPerCell: design.numberOfSeedsPerCell,
      numberOfTreatments: design.numberOfTreatments,
      sprayStudy: design.sprayStudy,
      seedNeeds: seedNeeds,
      recurrencePattern: design.recurrencePattern,
      recurrenceWeekInterval: design.recurrenceWeekInterval,
      recurrenceDays: this.toFormModel(design.recurrenceDays),
      recurrenceEndDate: design.recurrenceEndDate
        ? new Date(design.recurrenceEndDate)
        : null,
      harvestInstructions: {
        embryoRescue: design.embryoRescue,
        minimumSeedsToReturn: design.minimumSeedsToReturn,
        forAdvancement: design.advancementPreference.forAdvancement,
        haploidInduction: design.advancementPreference.haploidInduction,
        manyToOne: design.advancementPreference.manyToOne,
        uniquePedigree: design.advancementPreference.uniquePedigree
      },
      pollinationInstructions: pollinationInstructions,
      requestedSamplings: samplingsArray,
      indexing: design.indexing,
      treatments: treatmentArray,
      seedRequestDesired: design.seedRequestDesired,
      pesticidesAllowed: design.pesticideInstructionId,
      pesticidesAllowedAfterDays: design.pesticidesAllowedAfterDays,
      pesticideLifeOfPlant: design.pesticideLifeOfPlant
    };
  }

  public toDetailsApiModel(details: ExperimentDetailsFormModel): RequestDetails {
    return {
      id: null,
      ceSiteId: details.ceSite,
      cropId: details.crop,
      description: details.description,
      delegateId: details.delegate,
      productConceptIds: isNullOrUndefined(details.productConcept) ? [] : [details.productConcept],
      title: details.title,
      programKey: details.pmoProgram,
      projectKey: details.pmoProject,
      requestPurposeId: details.requestPurpose,
      requestedPlantingDate: details.plantingDate,
      requestedSeedPacketDate: details.seedPacketDate,
      weeksOfColdTreatment: details.weeksOfColdTreatment,
      regulatoryStatus: details.regulatoryStatus,
      ppqRequired: details.ppqRequired,
      phytoInspectionRequired: details.phytoInspectionRequired
    };
  }

  public toDesignApiModel(design: ExperimentDesignFormModel): RequestDesign {
    let requestedSeedModels: RequestedSeed[] = [];

    if (design.seedNeeds && design.seedNeeds.entries) {
      requestedSeedModels = asEnumerable(design.seedNeeds)
        .OrderBy(s => s.entryNumber)
        .Select(s => {
          return <RequestedSeed>{
            entryNumber: s.entryNumber,
            siid: s.siid,
            geName: s.geName,
            numberOfSeeds: s.numberOfSeeds,
            treatments: asEnumerable(s.treatments)
              .Select(t => <RequestedSeedTreatment>{
                treatmentNumber: t.treatmentNumber,
                seedCount: t.seedCount
              })
              .ToArray()
          };
        })
        .ToArray();
    }

    let requestedSamplings: RequestedSampling[] = [];

    if (design.requestedSamplings && design.requestedSamplings.length) {
      requestedSamplings = asEnumerable(design.requestedSamplings)
        .OrderBy(s => s.order)
        .Select(s => {
          return <RequestedSampling>{
            laboratoryAssayId: s.assay,
            laboratoryId: s.laboratory,
            order: s.order,
            tissueTypeId: s.tissueType,
            tissueCollectionType: s.tissueCollectionType,
            growthStageId: s.growthStage,
            daysAfterPlanting: s.daysAfterPlanting
          };
        })
        .ToArray();
    }

    const treatmentModels: Treatment[] = [];

    if (design.treatments) {
      design.treatments.forEach(t => {
        let treatmentModel: Treatment = {
          treatmentNumber: t.treatmentNumber
        };
        if (t.plantingInstructions) {
          treatmentModel.plantingInstructions = {
            plantsToHarvest: t.plantingInstructions.numberToHarvest,
            plantsToPots: t.plantingInstructions.numberToPots,
            seedsToStart: t.plantingInstructions.numberToStart
          };
        }
        if (t.sprayBoothStudy && Array.isArray(t.sprayBoothStudy)) {
          treatmentModel.sprayBoothStudy = t.sprayBoothStudy.map(s => ({
            actives: s.actives.map((a: ActiveFormModel) => ({
              activeId: a.activeId,
              rateValue: a.rateValue,
              rateUnitId: a.rateUnitId,
              relativeFieldRate: a.relativeFieldRate
            })),
            adjuvants: s.adjuvants.map((a: AdjuvantFormModel) => ({
              adjuvantId: a.adjuvantId,
              adjuvantValue: a.adjuvantValue,
            })),
            applicationTimepointId: s.applicationTimepointId,
            applicationVolumeId: s.applicationVolumeId,
            sprayEventNumber: s.sprayEventNumber,
            sprayEventDaysAfterPlanting: s.sprayEventDaysAfterPlanting
          }));
        }
        if (t.flatInstructions) {
          const directSeedToPots = t.flatInstructions.directSeedToPots;

          treatmentModel.flatInstructions = {
            directSeedToPots: directSeedToPots,
            flatTypeId: directSeedToPots ? null : t.flatInstructions.flatType,
            fertigationMethodId: directSeedToPots ? null : t.flatInstructions.fertigation,
            irrigationMethodId: directSeedToPots ? null : t.flatInstructions.irrigation,
            substrateId: directSeedToPots ? null : t.flatInstructions.substrate,
          }
        }
        if (t.pottingInstructions) {
          treatmentModel.pottingInstructions = {
            potTypeId: t.pottingInstructions.potType,
            fertigationMethodId: t.pottingInstructions.fertigation,
            irrigationMethodId: t.pottingInstructions.irrigation,
            substrateId: t.pottingInstructions.substrate
          };
        }
        else { treatmentModel.pottingInstructions = null }
        if (t.plantHandlingInstructions) {
          treatmentModel.plantHandlingInstructions = {
            lightingInstructionId: t.plantHandlingInstructions.lighting,
            temperatureInstructionId: t.plantHandlingInstructions.temperature,
            humidityInstructionId: t.plantHandlingInstructions.humidity,
            imagingNeeded: t.plantHandlingInstructions.imagingNeeded,
          };
        }
        treatmentModels.push(treatmentModel);
      });
    }

    let pollinationInstructions: PollinationInstructions;
    if (design.pollinationInstructions) {
      pollinationInstructions = {
        preferences: asEnumerable(design.pollinationInstructions.pollinationPreferences)
          .Select((g, groupIndex) => <PollinationPreferenceGroup>{
            orderNumber: groupIndex + 1,
            conjunction: g.conjunction,
            items: asEnumerable(g.items)
              .Select((item, itemIndex) => <PollinationPreferenceGroupItem>{
                orderNumber: itemIndex + 1,
                conjunction: item.conjunction,
                pollinationTypeId: item.pollinationType,
                geName: item.geName
              })
              .ToArray()
          })
          .ToArray(),
        donorsNeeded: design.pollinationInstructions.donorsNeeded,
        pollinationCategoryId: design.pollinationInstructions.pollinationCategory
      };
    }

    let advancementPreference: AdvancementPreference = {
      forAdvancement: true,
      haploidInduction: false,
      manyToOne: false,
      uniquePedigree: false
    };

    if (design.harvestInstructions) {
      advancementPreference = {
        forAdvancement: design.harvestInstructions.forAdvancement,
        haploidInduction: design.harvestInstructions.haploidInduction,
        manyToOne: design.harvestInstructions.manyToOne,
        uniquePedigree: design.harvestInstructions.uniquePedigree
      };
    }

    return {
      id: null,
      numberOfReps: design.numberOfReps,
      numberOfSeedsPerCell: design.numberOfSeedsPerCell,
      numberOfTreatments: design.numberOfTreatments,
      recurrencePattern: design.recurrencePattern,
      recurrenceWeekInterval: design.recurrenceWeekInterval,
      recurrenceDays: this.toApiModel(design.recurrenceDays),
      recurrenceEndDate: design.recurrenceEndDate,
      embryoRescue: design.harvestInstructions ? design.harvestInstructions.embryoRescue : false,
      minimumSeedsToReturn: design.harvestInstructions ? design.harvestInstructions.minimumSeedsToReturn : null,
      advancementPreference,
      requestedSeed: requestedSeedModels,
      indexing: design.indexing,
      pollinationInstructions: pollinationInstructions,
      requestedSamplings: requestedSamplings,
      treatments: treatmentModels,
      seedRequestDesired: design.seedRequestDesired,
      sprayStudy: design.sprayStudy,
      originatingSystem: OriginatingSystem.MyCE,
      pesticideInstructionId: design.pesticidesAllowed,
      pesticidesAllowedAfterDays: design.pesticidesAllowedAfterDays,
      pesticideLifeOfPlant: design.pesticideLifeOfPlant == null ? false : design.pesticideLifeOfPlant,
    };
  }

  private toApiModel(daySelections: boolean[]): RecurrenceDays {
    let result = RecurrenceDays.None;
    daySelections.forEach((d, index) => {
      result += ((d ? 1 : 0) << index);
    })

    return result;
  }

  private toFormModel(recurrenceDays: RecurrenceDays): boolean[] {
    let daySelections: boolean[] = [
      (recurrenceDays & RecurrenceDays.Monday) === RecurrenceDays.Monday,
      (recurrenceDays & RecurrenceDays.Tuesday) === RecurrenceDays.Tuesday,
      (recurrenceDays & RecurrenceDays.Wednesday) === RecurrenceDays.Wednesday,
      (recurrenceDays & RecurrenceDays.Thursday) === RecurrenceDays.Thursday,
      (recurrenceDays & RecurrenceDays.Friday) === RecurrenceDays.Friday,
    ];

    return daySelections;
  }
}



// WEBPACK FOOTER //
// F:/PHAZP5152IS01-4/_work/202/s/src/app/request-creation/services/request-form-model.service.ts